import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bookmark, Loader2, MessageSquare } from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../utils/useIsMobile';
import OrganicShell from '../components/organic/OrganicShell';
import ShareBrandModal from '../components/ShareBrandModal';
import AskPanel from '../components/movers/AskPanel';
import { BrandTagEditor } from '../components/BrandTags';

// Movers — the redesigned dashboard and the rep's first screen each
// morning: which brands are breaking out right now, split into columns,
// with the next action on each card. Replaces the old stat cards +
// Focus Queue + growth tables page.
//
// The columns are cut by region by default, or by territory once the
// org has drawn any (Settings → Territories) — same cards, grouped the
// way the team is actually organised, with an "Only mine" chip that
// narrows the board to the countries the signed-in rep owns.
//
// On a phone there is no room for a column per region, so the regions
// become a chip rail over a single feed ranked by the same measure the
// columns are ranked on, and the docked Ask panel becomes a sheet.

const STROKE = 2.75;

// Region colour split is load-bearing: North America reads in the
// terracotta accent ramp, Europe in the sage accent-2 ramp, so the
// columns tell apart at a glance. Further regions alternate.
const RAMPS = [
  { value: 'var(--color-accent-700)', bar: 'var(--color-accent)', barMid: 'var(--color-accent-300)', outline: 'var(--color-accent-400)' },
  { value: 'var(--color-accent-2-700)', bar: 'var(--color-accent-2)', barMid: 'var(--color-accent-2-300)', outline: 'var(--color-accent-2-400)' },
];

function rampFor(slug, index) {
  if (slug === 'north_america') return RAMPS[0];
  if (slug === 'europe') return RAMPS[1];
  return RAMPS[index % 2];
}

// A territory carries its own chip colour from Settings, so its column
// is drawn in that colour rather than the two-region alternation. The
// fallback keeps a territory with no colour looking like a region.
function rampForBucket(bucket, index) {
  if (!bucket.color) return rampFor(bucket.slug, index);
  return {
    value: bucket.color,
    bar: bucket.color,
    barMid: `color-mix(in srgb, ${bucket.color} 45%, transparent)`,
    outline: `color-mix(in srgb, ${bucket.color} 70%, transparent)`,
  };
}

function formatFollowers(num) {
  if (num == null) return '—';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return String(num);
}

function syncedAgo(iso) {
  if (!iso) return null;
  const hours = Math.round((Date.now() - new Date(iso).getTime()) / 3600000);
  if (hours < 1) return 'synced under an hour ago';
  if (hours < 48) return `synced ${hours} hour${hours === 1 ? '' : 's'} ago`;
  return `synced ${Math.round(hours / 24)} days ago`;
}

function Sparkline({ series, ramp, caption }) {
  // Bars are week-over-week follower gains, not cumulative counts — the
  // shape of the run-up is what the card is for.
  const bars = useMemo(() => {
    if (!series || series.length < 3) return null;
    const deltas = series.slice(1).map((v, i) => v - series[i]);
    const min = Math.min(...deltas);
    const max = Math.max(...deltas);
    const span = max - min || 1;
    return deltas.map((d) => 18 + ((d - min) / span) * 82);
  }, [series]);
  if (!bars) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 34 }}>
      {bars.map((h, j) => (
        <div
          key={j}
          style={{
            width: 12,
            borderRadius: 999,
            height: `${h}%`,
            background: j === bars.length - 1
              ? ramp.bar
              : j === bars.length - 2
                ? ramp.barMid
                : 'var(--color-neutral-300)',
          }}
        />
      ))}
      {caption && <span style={{ fontSize: 11, opacity: 0.55, alignSelf: 'center', marginLeft: 6 }}>last 6 weeks</span>}
    </div>
  );
}

function MoverCard({ mover, ramp, highlight, showCaption, compact, onShare, onTagsUpdated }) {
  const navigate = useNavigate();
  // When the org ranks by a custom score, that score is the headline —
  // showing growth % as the big number next to a score-ordered list is
  // what made the ordering look arbitrary. Growth keeps its detail on
  // the line below. With no custom score, growth stays the headline.
  const baseline = mover.baseline_multiple
    ? `${mover.baseline_multiple}× baseline`
    : 'no baseline yet';
  const scored = mover.score != null;
  // Listed for its score, not its growth (the org opted in on Settings →
  // Scoring), so the growth line says plainly that it didn't move.
  const flatNote = mover.growth_pct == null
    ? 'no new data this week'
    : mover.growth_pct === 0 ? 'flat this week' : `${mover.growth_pct}% this week`;
  return (
    <div className="card" style={{ gap: 'var(--space-3)', ...(highlight ? { outline: `2px solid ${ramp.outline}` } : {}) }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18 }}>{mover.name}</div>
          <div style={{ fontSize: 12, opacity: 0.65 }}>
            {/* HQ country sits with the category: procurement and R&D
                are there, so it is the first thing that decides whose
                card this is. Dropped only when the brand has none. */}
            {[mover.category, mover.country].filter(Boolean).join(' · ')}
            {' · '}{formatFollowers(mover.followers)} followers
          </div>
        </div>
        <div style={{ textAlign: 'right', flex: 'none' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 26, color: ramp.value, lineHeight: 1 }}>
            {scored ? Math.round(mover.score) : `+${mover.growth_pct}%`}
            {/* The "/100" is load-bearing: it stops a 0-100 fit score
                from reading as a percentage next to the growth figures. */}
            {scored && <span style={{ fontSize: 14, opacity: 0.5 }}>/100</span>}
          </div>
          <div style={{ fontSize: 11, opacity: 0.6 }}>
            {mover.flat ? flatNote : scored ? `+${mover.growth_pct}% · ${baseline}` : baseline}
          </div>
        </div>
      </div>
      <Sparkline series={mover.sparkline} ramp={ramp} caption={showCaption} />
      {/* Triage happens here — this screen is where a rep decides "that
          one's a target" — so the tag goes on from the card rather than
          costing a round trip through the brand page. */}
      <BrandTagEditor
        brand={{ id: mover.brand_id, name: mover.name, tags: mover.tags }}
        onBrandUpdated={onTagsUpdated}
      />
      {/* On a phone the two actions split the width evenly and the
          follow button keeps its own 44px target at the end. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: compact ? 'nowrap' : 'wrap' }}>
        <button
          type="button"
          className="btn btn-primary"
          style={compact ? { flex: 1, height: 44, minWidth: 0 } : { fontSize: 13 }}
          onClick={() => navigate(`/brands/${mover.brand_id}#contacts`)}
        >
          Find contact
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          style={compact ? { flex: 1, height: 44, minWidth: 0 } : { fontSize: 13 }}
          onClick={() => navigate(`/brands/${mover.brand_id}`)}
        >
          {compact ? 'Open' : 'Open brand'}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          title="Follow — share the report"
          aria-label={`Follow ${mover.name}`}
          style={compact ? { width: 44, height: 44, flex: 'none' } : { marginLeft: 'auto' }}
          onClick={() => onShare(mover)}
        >
          <Bookmark size={15} strokeWidth={STROKE} />
        </button>
      </div>
    </div>
  );
}

function Movers() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [platform, setPlatform] = useState('instagram');
  // How the columns are cut, remembered across visits — a rep who works
  // one territory shouldn't have to re-pick it every morning.
  const [groupBy, setGroupBy] = useState(
    () => (localStorage.getItem('movers_group_by') === 'territory' ? 'territory' : 'region'),
  );
  const [onlyMine, setOnlyMine] = useState(false);
  // Whether this org has drawn any territories at all. Without it the
  // toggle would offer a cut that produces one "Unassigned" column.
  const [hasTerritories, setHasTerritories] = useState(false);
  const [regionFilter, setRegionFilter] = useState(''); // phone only: '' = all
  const [askSheet, setAskSheet] = useState(false); // phone only
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharing, setSharing] = useState(null); // mover being shared/followed
  // The docked Ask panel, remembered across visits ('open' | 'collapsed').
  const [askOpen, setAskOpen] = useState(() => localStorage.getItem('movers_ask_panel') !== 'collapsed');

  const setAsk = (open) => {
    setAskOpen(open);
    localStorage.setItem('movers_ask_panel', open ? 'open' : 'collapsed');
  };

  const load = useCallback(async (p, cut) => {
    try {
      setLoading(true);
      setData(await api.getMovers(p, 9, cut));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(platform, groupBy); }, [platform, groupBy, load]);

  // One extra call on mount, only to decide whether the cut toggle is
  // worth showing. A failure here leaves the board grouped by region,
  // which is what it did before territories existed.
  useEffect(() => {
    let live = true;
    api.listTerritories()
      .then((res) => { if (live) setHasTerritories((res.territories || []).length > 0); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const selectCut = (cut) => {
    setGroupBy(cut);
    localStorage.setItem('movers_group_by', cut);
    if (cut === 'region') setOnlyMine(false);
  };

  // A tag added on a card patches that brand in place: the phone feed
  // and the desktop columns both derive from `data`, so re-fetching
  // the whole board (and re-ordering it under the user) isn't needed.
  const handleTagsUpdated = useCallback((updated) => {
    setData((prev) => (prev ? {
      ...prev,
      regions: prev.regions.map((region) => ({
        ...region,
        movers: region.movers.map((m) => (
          m.brand_id === updated.id ? { ...m, tags: updated.tags || [] } : m
        )),
      })),
    } : prev));
  }, []);

  // Read the cut off the response rather than the state, so a board
  // still showing the previous grouping is labelled by what it holds.
  const byTerritory = data?.group_by === 'territory';
  const myEmail = (user?.email || '').toLowerCase();
  const allBuckets = useMemo(() => data?.regions || [], [data]);
  // Whether "Only mine" would show anything — offering it to someone
  // who owns no territory is a chip that empties the screen. Local-auth
  // dev users carry no email, so it simply never appears there.
  const ownsAny = useMemo(
    () => byTerritory && !!myEmail
      && allBuckets.some((b) => (b.owner?.email || '').toLowerCase() === myEmail),
    [allBuckets, byTerritory, myEmail],
  );
  const regions = useMemo(() => (
    byTerritory && onlyMine
      ? allBuckets.filter((b) => (b.owner?.email || '').toLowerCase() === myEmail)
      : allBuckets
  ), [allBuckets, byTerritory, onlyMine, myEmail]);

  // "region" in the copy below is whatever the board is cut by.
  const cutNoun = byTerritory ? 'territory' : 'region';
  const cutPlural = byTerritory ? 'territories' : 'regions';

  // A region with nothing breaking out earns a footnote, not a column:
  // the grid is auto-fit, so dropping an empty column widens the ones
  // that have cards in them. The region is still named below the grid
  // so "quiet this week" stays distinguishable from "not tracked".
  const shownRegions = useMemo(
    () => regions.filter((r) => r.movers.length > 0), [regions],
  );
  const quietRegions = useMemo(
    () => regions.filter((r) => r.movers.length === 0), [regions],
  );

  // The single biggest move across all regions gets an outline — by the
  // same measure the cards are ranked on, so the outline never lands on
  // a card sitting below its neighbours.
  const biggest = useMemo(() => {
    const rank = (m) => (m.score != null ? m.score : m.growth_pct);
    let best = null;
    for (const region of regions) {
      const top = region.movers[0];
      if (top && (!best || rank(top) > rank(best))) best = top;
    }
    return best;
  }, [regions]);

  const subhead = [
    data?.score_name
      ? `Ranked by ${data.score_name}, your custom score from Settings${data.include_flat ? ', including top brands that didn’t grow this week' : ''}.`
      : "Follower growth against each brand's own four-week baseline.",
    data ? `${data.total_brands} brands watched` : null,
    syncedAgo(data?.synced_at),
  ].filter(Boolean).join(' ').replace('watched synced', 'watched, synced');

  // Phone: one feed instead of a column per region, ranked by the same
  // measure the columns are — the region chips filter it rather than
  // splitting it. Each mover keeps its own region's colour ramp so the
  // ramp still reads as "which region is this".
  //
  // One chip row, cut by whatever the board is grouped by. HQ country
  // used to get a second row under it; three stacked filter rows on a
  // phone cost more screen than the cut earned, and a rep who works a
  // set of countries has a territory for exactly that.
  const inRegion = useMemo(() => (
    shownRegions.flatMap((region, ri) => (
      regionFilter && region.slug !== regionFilter
        ? []
        : region.movers.map((mover) => ({ mover, ramp: rampForBucket(region, ri) }))
    ))
  ), [shownRegions, regionFilter]);

  const feed = useMemo(() => {
    const rank = (m) => (m.score != null ? m.score : m.growth_pct);
    return [...inRegion].sort((a, b) => rank(b.mover) - rank(a.mover));
  }, [inRegion]);

  // A chip whose region has scrolled out of the data resets to "all".
  useEffect(() => {
    if (regionFilter && !shownRegions.some((r) => r.slug === regionFilter)) setRegionFilter('');
  }, [shownRegions, regionFilter]);

  const cutControls = (hasTerritories || ownsAny) ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
      {hasTerritories && (
        <div className="seg" role="group" aria-label="Group columns by">
          <label className="seg-opt">
            <input
              type="radio"
              name="group-by"
              checked={groupBy === 'region'}
              onChange={() => selectCut('region')}
            />
            By region
          </label>
          <label className="seg-opt">
            <input
              type="radio"
              name="group-by"
              checked={groupBy === 'territory'}
              onChange={() => selectCut('territory')}
            />
            By territory
          </label>
        </div>
      )}
      {ownsAny && (
        <button
          type="button"
          className={`o-chip${onlyMine ? ' is-on' : ''}`}
          aria-pressed={onlyMine}
          onClick={() => setOnlyMine((v) => !v)}
        >
          Only mine
        </button>
      )}
    </div>
  ) : null;

  const moverCount = regions.reduce((sum, r) => sum + r.movers.length, 0);
  const askVisible = isMobile ? askSheet : askOpen;
  const pane = askVisible && shownRegions.length > 0
    ? (
      <AskPanel
        regions={shownRegions}
        sheet={isMobile}
        onCollapse={() => (isMobile ? setAskSheet(false) : setAsk(false))}
      />
    )
    : null;

  return (
    <OrganicShell active="movers" contentStyle={{ maxWidth: 1400, padding: 'var(--space-8)', gap: 'var(--space-6)' }} aside={pane}>
      <header style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-4) var(--space-6)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
          <h1 className="o-page-title">Who's moving</h1>
          <p className="o-page-subhead">{subhead}</p>
        </div>
        {isMobile && (cutControls || shownRegions.length > 1) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', minWidth: 0 }}>
            {cutControls}
            {shownRegions.length > 1 && (
              <div className="o-chip-strip" role="group" aria-label={`Filter by ${cutNoun}`}>
                <button
                  type="button"
                  className={`o-chip${regionFilter === '' ? ' is-on' : ''}`}
                  aria-pressed={regionFilter === ''}
                  onClick={() => setRegionFilter('')}
                >
                  All {cutPlural}
                </button>
                {shownRegions.map((region) => (
                  <button
                    key={region.slug}
                    type="button"
                    className={`o-chip${regionFilter === region.slug ? ' is-on' : ''}`}
                    aria-pressed={regionFilter === region.slug}
                    onClick={() => setRegionFilter(region.slug)}
                  >
                    {region.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {!isMobile && cutControls}
          <div className="seg">
            <label className="seg-opt">
              <input
                type="radio"
                name="platform"
                checked={platform === 'instagram'}
                onChange={() => setPlatform('instagram')}
              />
              Instagram
            </label>
            <label className="seg-opt">
              <input
                type="radio"
                name="platform"
                checked={platform === 'tiktok'}
                onChange={() => setPlatform('tiktok')}
              />
              TikTok
            </label>
          </div>
        </div>
      </header>

      {loading && !data && (
        <div className="card" style={{ padding: 'var(--space-4)', flexDirection: 'row', alignItems: 'center', gap: 8, fontSize: 14, opacity: 0.75 }}>
          <Loader2 size={16} strokeWidth={STROKE} className="spin" /> Loading movers…
        </div>
      )}
      {error && (
        <div className="card" style={{ padding: 'var(--space-4)', color: 'var(--color-accent-700)', fontSize: 14 }}>
          Error: {error}
        </div>
      )}

      {data && isMobile && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {feed.map(({ mover, ramp }, i) => (
            <MoverCard
              key={mover.brand_id}
              mover={mover}
              ramp={ramp}
              highlight={biggest && mover.brand_id === biggest.brand_id}
              showCaption={i === 0}
              compact
              onShare={setSharing}
              onTagsUpdated={handleTagsUpdated}
            />
          ))}
          {feed.length === 0 && (
            <div className="card" style={{ fontSize: 14, opacity: 0.75 }}>
              {regions.length === 0
                ? 'No follower history yet — movers appear once the weekly sync has a few weeks of data.'
                : regionFilter
                  ? `Nothing is breaking out this week in that ${cutNoun}.`
                  : `Nothing is breaking out this week in any ${cutNoun}.`}
            </div>
          )}
          {feed.length > 0 && (
            <Link to="/brands" style={{ fontSize: 13, paddingLeft: 'var(--space-2)' }}>
              All {moverCount} movers
            </Link>
          )}
        </div>
      )}

      {data && !isMobile && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 'var(--space-6)' }}>
          {shownRegions.map((region, ri) => {
            const ramp = rampForBucket(region, ri);
            return (
              <section key={region.slug} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  <h4 style={{ margin: 0 }}>{region.label}</h4>
                  {/* Who to hand a card to is the first thing you need
                      from a territory column, so it sits in the heading
                      rather than behind a hover. */}
                  {byTerritory && (
                    <span style={{ fontSize: 12, opacity: 0.75 }}>
                      {region.owner?.name || region.owner?.email || 'unassigned'}
                    </span>
                  )}
                  <span style={{ fontSize: 12, opacity: 0.6 }}>
                    {region.brand_count} brands · {region.breaking_out} breaking out
                  </span>
                </div>

                {region.movers.map((mover, i) => (
                  <MoverCard
                    key={mover.brand_id}
                    mover={mover}
                    ramp={ramp}
                    highlight={biggest && mover.brand_id === biggest.brand_id}
                    showCaption={i === 0}
                    onShare={setSharing}
                    onTagsUpdated={handleTagsUpdated}
                  />
                ))}

                <Link to="/brands" style={{ fontSize: 13, paddingLeft: 'var(--space-2)' }}>
                  All {region.movers.length} {region.label} movers
                </Link>
              </section>
            );
          })}
          {shownRegions.length === 0 && (
            <div className="card" style={{ padding: 'var(--space-4)', fontSize: 14, opacity: 0.75 }}>
              {regions.length === 0
                ? 'No follower history yet — movers appear once the weekly sync has a few weeks of data.'
                : `Nothing is breaking out this week in any ${cutNoun}.`}
            </div>
          )}
        </div>
      )}

      {data && !isMobile && quietRegions.length > 0 && shownRegions.length > 0 && (
        <p style={{ margin: 0, fontSize: 13, opacity: 0.6 }}>
          Nothing moving this week in{' '}
          {quietRegions
            .map((r) => `${r.label} (${r.brand_count} ${r.brand_count === 1 ? 'brand' : 'brands'})`)
            .join(', ')}
          .
        </p>
      )}

      {!askVisible && moverCount > 0 && (
        <button
          type="button"
          className="btn btn-primary elev-lg"
          style={{
            alignSelf: isMobile ? 'stretch' : 'flex-end',
            height: isMobile ? 48 : undefined,
            padding: 'var(--space-3) var(--space-4)',
            fontSize: 14,
          }}
          onClick={() => (isMobile ? setAskSheet(true) : setAsk(true))}
        >
          <MessageSquare size={17} strokeWidth={2.75} /> Ask about these {moverCount}
        </button>
      )}

      {sharing && (
        <ShareBrandModal
          brand={{ id: sharing.brand_id, name: sharing.name }}
          onClose={() => setSharing(null)}
          onShared={() => setSharing(null)}
        />
      )}
    </OrganicShell>
  );
}

export default Movers;
